import { useState, useEffect, useMemo } from 'react'
import { PurchaseRequest } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import { 
  loadPurchaseRequests, 
  approvePurchaseRequestByEconomist,
  rejectPurchaseRequestByEconomist,
  deferPurchaseRequest
} from '../lib/api'
import PurchaseRequestsTable from '../components/PurchaseRequestsTable'
import PurchaseRequestCard from '../components/PurchaseRequestCard'
import Pagination from '../components/Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import { protectedFileHref } from '../utils/fileDownload'
import './FinancistView.css'
import '../styles/common-buttons.css'

interface FinancistViewProps {
  purchaseRole?: string
}

function FinancistView({ purchaseRole = 'financist' }: FinancistViewProps) {
  const { user } = useAuth()
  const { showAlert } = useAlert()
  const effectivePurchaseRole = purchaseRole || 'financist'
  const [requests, setRequests] = useState<PurchaseRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null)
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'defer' | null>(null)
  const [notes, setNotes] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sectionPages, setSectionPages] = useState<Record<string, number>>({})
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    // Загружаем данные только если user готов
    if (user) {
      loadRequestsData()
    } else {
      // Если user еще не загружен, сбрасываем loading
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, purchaseRole])

  const loadRequestsData = async () => {
    if (!user) {
      setLoading(false)
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      const effectiveRole = purchaseRole || user.role
      const data = await loadPurchaseRequests(user.id, user.role ?? null, effectiveRole)
      setRequests(data || [])
    } catch (error: any) {
      console.error('Chyba při načítání žádostí:', error)
      setRequests([])
      // Сохраняем сообщение об ошибке для отображения
      const errorMessage = error?.message || 'Chyba při načítání žádostí'
      setError(errorMessage.includes('Database') || errorMessage.includes('базы данных') 
        ? 'Chyba připojení k databázi. Zkuste obnovit stránku.' 
        : errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: !loading,
    onFound: (r) => {
      setStatusFilter(r.status)
      setSelectedRequest(r)
      setActionType(null)
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již byla zpracována.', 'error'),
    elementIdFor: (id) => `purchase-${id}`,
  })

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return

    try {
      if (actionType === 'approve') {
        await approvePurchaseRequestByEconomist(selectedRequest.id, notes || undefined, effectivePurchaseRole)
      } else if (actionType === 'reject') {
        await rejectPurchaseRequestByEconomist(selectedRequest.id, notes || undefined, effectivePurchaseRole)
      } else if (actionType === 'defer') {
        await deferPurchaseRequest(selectedRequest.id, notes || undefined, effectivePurchaseRole)
      }
      
      setSelectedRequest(null)
      setActionType(null)
      setNotes('')
      await loadRequestsData()
      alert('Akce byla úspěšně provedena')
    } catch (error: any) {
      console.error('Chyba při provádění akce:', error)
      const errorMessage = error?.message || error?.details || 'Neznámá chyba'
      alert(`Chyba při provádění akce: ${errorMessage}`)
    }
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'new': 'Nová',
      'deferred': 'Odloženo ekonomem',
      'approved_by_economist': 'Schváleno ekonomem',
      'rejected': 'Zamítnuto',
      'rejected_by_economist': 'Zamítnuto ekonomem',
      'approved_by_director': 'Schváleno ředitelem',
      'rejected_by_director': 'Zamítnuto ředitelem',
      'accepted_by_referent': 'Přijato obchodním referentem',
      'ordered_by_referent': 'Objednáno obchodním referentem',
      'ready_by_referent': 'Připraveno obchodním referentem',
      'completed': 'Dokončeno'
    }
    return labels[status] || status
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'deferred':
        return 'status-deferred'
      case 'approved_by_economist':
      case 'approved_by_director':
        return 'status-approved'
      case 'rejected':
      case 'rejected_by_economist':
      case 'rejected_by_director':
        return 'status-rejected'
      case 'accepted_by_referent':
        return 'status-accepted'
      case 'ordered_by_referent':
        return 'status-ordered'
      case 'ready_by_referent':
        return 'status-ready'
      case 'completed':
        return 'status-completed'
      default:
        return ''
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('cs-CZ')
  }

  const handleTableAction = async (request: PurchaseRequest, action: string) => {
    setSelectedRequest(request)
    if (action === 'approve') {
      setActionType('approve')
    } else if (action === 'reject') {
      setActionType('reject')
    } else if (action === 'defer') {
      setActionType('defer')
    }
    setNotes('')
  }

  // Фильтрация заявок только по поисковому тексту (для статистики)
  const requestsForStats = useMemo(() => {
    if (!requests || requests.length === 0) return []
    if (!searchText.trim()) return requests
    
    const searchLower = searchText.toLowerCase().trim()
    return requests.filter(r => {
      const subject = (r.subject || '').toLowerCase()
      const description = (r.description || '').toLowerCase()
      const reason = (r.reason || '').toLowerCase()
      const supplier = (r.supplier || '').toLowerCase()
      const centerName = (r.center_name || '').toLowerCase()
      const createdByName = (r.created_by_name || '').toLowerCase()
      const notes = (r.notes || '').toLowerCase()
      
      return subject.includes(searchLower) ||
             description.includes(searchLower) ||
             reason.includes(searchLower) ||
             supplier.includes(searchLower) ||
             centerName.includes(searchLower) ||
             createdByName.includes(searchLower) ||
             notes.includes(searchLower)
    })
  }, [requests, searchText])

  // Фильтрация заявок по выбранному статусу и поисковому тексту (для отображения)
  const filteredRequests = useMemo(() => {
    let filtered = requestsForStats
    
    // Фильтр по статусу
    if (statusFilter) {
      filtered = filtered.filter(r => {
        if (statusFilter === 'new') return r.status === 'new'
        if (statusFilter === 'deferred') return r.status === 'deferred'
        if (statusFilter === 'approved_by_economist') return r.status === 'approved_by_economist'
        if (statusFilter === 'rejected_by_economist') return r.status === 'rejected_by_economist'
        if (statusFilter === 'approved_by_director') return r.status === 'approved_by_director'
        if (statusFilter === 'rejected_by_director') return r.status === 'rejected_by_director'
        if (statusFilter === 'accepted_by_referent') return r.status === 'accepted_by_referent'
        if (statusFilter === 'ordered_by_referent') return r.status === 'ordered_by_referent'
        if (statusFilter === 'completed') return r.status === 'completed'
        return true
      })
    }
    
    return filtered
  }, [requestsForStats, statusFilter])

  const newRequests = useMemo(() => requestsForStats.filter(r => r.status === 'new'), [requestsForStats])
  const deferredRequests = useMemo(() => requestsForStats.filter(r => r.status === 'deferred'), [requestsForStats])
  const approvedByEconomistRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_economist'), [requestsForStats])
  const rejectedByEconomistRequests = useMemo(() => requestsForStats.filter(r => r.status === 'rejected_by_economist'), [requestsForStats])
  const approvedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'approved_by_director'), [requestsForStats])
  const rejectedByDirectorRequests = useMemo(() => requestsForStats.filter(r => r.status === 'rejected_by_director'), [requestsForStats])
  const acceptedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'accepted_by_referent'), [requestsForStats])
  const orderedByReferentRequests = useMemo(() => requestsForStats.filter(r => r.status === 'ordered_by_referent'), [requestsForStats])
  const completedRequests = useMemo(() => requestsForStats.filter(r => r.status === 'completed'), [requestsForStats])

  // Функции для пагинации
  const getPaginatedRequests = (items: PurchaseRequest[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return items.slice(startIndex, endIndex)
  }

  const getTotalPages = (items: PurchaseRequest[]) => {
    return Math.ceil(items.length / itemsPerPage)
  }

  const getSectionPage = (sectionKey: string) => {
    return sectionPages[sectionKey] || 1
  }

  const setSectionPage = (sectionKey: string, page: number) => {
    setSectionPages(prev => ({ ...prev, [sectionKey]: page }))
  }

  // Сбрасываем страницу при изменении фильтра или поиска
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, searchText])

  // Показываем загрузку только если user есть, но данные еще загружаются
  if (loading && user) {
    return <div className="financist-view">Načítání...</div>
  }
  
  // Если user не загружен, показываем загрузку (App.tsx должен обработать это)
  if (!user) {
    return <div className="financist-view">Načítání...</div>
  }

  return (
    <div className="financist-view">
      {error && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c33'
        }}>
          <strong>Chyba:</strong> {error}
          <button 
            onClick={() => {
              setError(null)
              loadRequestsData()
            }}
            style={{
              marginLeft: '1rem',
              padding: '0.25rem 0.5rem',
              backgroundColor: '#c33',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Zkusit znovu
          </button>
        </div>
      )}
      <div className="view-controls">
        <button 
          className={viewMode === 'cards' ? 'active' : ''}
          onClick={() => setViewMode('cards')}
        >
          Karty
        </button>
        <button 
          className={viewMode === 'table' ? 'active' : ''}
          onClick={() => setViewMode('table')}
        >
          Tabulka
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            placeholder="Hledat podle textu..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              minWidth: '250px'
            }}
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer'
              }}
            >
              Vymazat
            </button>
          )}
        </div>
      </div>

      <div className="admin-stats">
        <div 
          className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Nové</h3>
          <p className="stat-number">{newRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'approved_by_economist' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'approved_by_economist' ? null : 'approved_by_economist')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Schváleno ekonomem</h3>
          <p className="stat-number">{approvedByEconomistRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'deferred' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'deferred' ? null : 'deferred')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Odloženo ekonomem</h3>
          <p className="stat-number">{deferredRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'rejected_by_economist' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'rejected_by_economist' ? null : 'rejected_by_economist')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Zamítnuto ekonomem</h3>
          <p className="stat-number">{rejectedByEconomistRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'approved_by_director' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'approved_by_director' ? null : 'approved_by_director')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Schváleno ředitelem</h3>
          <p className="stat-number">{approvedByDirectorRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'rejected_by_director' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'rejected_by_director' ? null : 'rejected_by_director')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Zamítnuto ředitelem</h3>
          <p className="stat-number">{rejectedByDirectorRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'accepted_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'accepted_by_referent' ? null : 'accepted_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Přijato obchodním referentem</h3>
          <p className="stat-number">{acceptedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'ordered_by_referent' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'ordered_by_referent' ? null : 'ordered_by_referent')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Objednáno obchodním referentem</h3>
          <p className="stat-number">{orderedByReferentRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Dokončeno</h3>
          <p className="stat-number">{completedRequests.length}</p>
        </div>
        <div 
          className={`stat-card ${statusFilter === null ? 'active' : ''}`}
          onClick={() => setStatusFilter(null)}
          style={{ cursor: 'pointer' }}
        >
          <h3>Celkem</h3>
          <p className="stat-number">{requestsForStats.length}</p>
        </div>
      </div>

      {viewMode === 'table' ? (
        <PurchaseRequestsTable 
          requests={filteredRequests}
          onAction={handleTableAction}
          filterType="financist"
          hideStatusFilter={true}
          statusFilter={statusFilter}
        />
      ) : statusFilter ? (
        <div className="requests-container">
          {statusFilter === 'new' && (
            <>
              <h2>Nové žádosti ({newRequests.length})</h2>
              {newRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(newRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                    />
                    ))}
                  </div>
                  {getTotalPages(newRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(newRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={newRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné nové žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'deferred' && (
            <>
              <h2>Odložené žádosti ({deferredRequests.length})</h2>
              {deferredRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(deferredRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      onAction={handleTableAction}
                      showActions={true}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                    />
                    ))}
                  </div>
                  {getTotalPages(deferredRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(deferredRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={deferredRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné odložené žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'approved_by_economist' && (
            <>
              <h2>Schváleno ekonomem ({approvedByEconomistRequests.length})</h2>
              {approvedByEconomistRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(approvedByEconomistRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(approvedByEconomistRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(approvedByEconomistRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={approvedByEconomistRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné schválené žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'rejected_by_economist' && (
            <>
              <h2>Zamítnuto ekonomem ({rejectedByEconomistRequests.length})</h2>
              {rejectedByEconomistRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(rejectedByEconomistRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(rejectedByEconomistRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(rejectedByEconomistRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={rejectedByEconomistRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné zamítnuté žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'approved_by_director' && (
            <>
              <h2>Schváleno ředitelem ({approvedByDirectorRequests.length})</h2>
              {approvedByDirectorRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(approvedByDirectorRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(approvedByDirectorRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(approvedByDirectorRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={approvedByDirectorRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné schválené žádosti ředitelem</p>
              )}
            </>
          )}
          {statusFilter === 'rejected_by_director' && (
            <>
              <h2>Zamítnuto ředitelem ({rejectedByDirectorRequests.length})</h2>
              {rejectedByDirectorRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(rejectedByDirectorRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(rejectedByDirectorRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(rejectedByDirectorRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={rejectedByDirectorRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné zamítnuté žádosti ředitelem</p>
              )}
            </>
          )}
          {statusFilter === 'accepted_by_referent' && (
            <>
              <h2>Přijato obchodním referentem ({acceptedByReferentRequests.length})</h2>
              {acceptedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(acceptedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(acceptedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(acceptedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={acceptedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné přijaté žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'ordered_by_referent' && (
            <>
              <h2>Objednáno obchodním referentem ({orderedByReferentRequests.length})</h2>
              {orderedByReferentRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(orderedByReferentRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(orderedByReferentRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(orderedByReferentRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={orderedByReferentRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné objednané žádosti</p>
              )}
            </>
          )}
          {statusFilter === 'completed' && (
            <>
              <h2>Dokončeno ({completedRequests.length})</h2>
              {completedRequests.length > 0 ? (
                <>
                  <div className="requests-list">
                    {getPaginatedRequests(completedRequests, currentPage).map(request => (
                    <PurchaseRequestCard
                      key={request.id}
                      request={request}
                      getStatusLabel={getStatusLabel}
                      getStatusClass={getStatusClass}
                      searchText={searchText}
                    />
                    ))}
                  </div>
                  {getTotalPages(completedRequests) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={getTotalPages(completedRequests)}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={completedRequests.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné dokončené žádosti</p>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="requests-container">
        <h2>Nové žádosti ({newRequests.length})</h2>
        {newRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(newRequests, getSectionPage('new')).map(request => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <h3>{request.subject}</h3>
                  <span className={`status-badge status-${request.status}`}>
                    {getStatusLabel(request.status)}
                  </span>
                </div>
                <div className="request-details">
                  <p><strong>Středisko:</strong> {request.center_name}</p>
                  <p><strong>Vytvořil:</strong> {request.created_by_name}</p>
                  <p><strong>Datum vytvoření:</strong> {formatDate(request.created_at)}</p>
                  <p><strong>Popis:</strong> {request.description}</p>
                  <p><strong>Důvod objednání:</strong> {request.reason}</p>
                  {request.supplier && <p><strong>Dodavatel:</strong> {request.supplier}</p>}
                  {request.amount && <p><strong>Částka:</strong> {request.amount.toLocaleString('cs-CZ')} Kč</p>}
                  {request.file_name && (
                    <p>
                      <strong>Příloha:</strong>{' '}
                      <a 
                        href={protectedFileHref(request.file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {request.file_name}
                      </a>
                    </p>
                  )}
                </div>
                <div className="request-actions">
                  <button 
                    className="btn-approve"
                    onClick={() => {
                      setSelectedRequest(request)
                      setActionType('approve')
                      setNotes('')
                    }}
                  >
                    Schválit
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={() => {
                      setSelectedRequest(request)
                      setActionType('reject')
                      setNotes('')
                    }}
                  >
                    Zamítnout
                  </button>
                  <button 
                    className="btn-defer"
                    onClick={() => {
                      setSelectedRequest(request)
                      setActionType('defer')
                      setNotes('')
                    }}
                  >
                    Odložit
                  </button>
                </div>
              </div>
              ))}
            </div>
            {getTotalPages(newRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('new')}
                totalPages={getTotalPages(newRequests)}
                onPageChange={(page) => setSectionPage('new', page)}
                itemsPerPage={itemsPerPage}
                totalItems={newRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné nové žádosti</p>
        )}

        <h2>Odložené žádosti ({deferredRequests.length})</h2>
        {deferredRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(deferredRequests, getSectionPage('deferred')).map(request => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <h3>{request.subject}</h3>
                  <span className={`status-badge status-${request.status}`}>
                    {getStatusLabel(request.status)}
                  </span>
                </div>
                <div className="request-details">
                  <p><strong>Středisko:</strong> {request.center_name}</p>
                  <p><strong>Vytvořil:</strong> {request.created_by_name}</p>
                  <p><strong>Datum vytvoření:</strong> {formatDate(request.created_at)}</p>
                  <p><strong>Odloženo:</strong> {formatDate(request.deferred_at)}</p>
                  {request.notes && (
                    <div className="request-notes-display">
                      <strong>Poznámky:</strong>
                      <div className="notes-content">{request.notes}</div>
                    </div>
                  )}
                  <p><strong>Popis:</strong> {request.description}</p>
                  <p><strong>Důvod objednání:</strong> {request.reason}</p>
                  {request.supplier && <p><strong>Dodavatel:</strong> {request.supplier}</p>}
                  {request.amount && <p><strong>Částka:</strong> {request.amount.toLocaleString('cs-CZ')} Kč</p>}
                </div>
                <div className="request-actions">
                  <button 
                    className="btn-approve"
                    onClick={() => {
                      setSelectedRequest(request)
                      setActionType('approve')
                      setNotes('')
                    }}
                  >
                    Schválit
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={() => {
                      setSelectedRequest(request)
                      setActionType('reject')
                      setNotes('')
                    }}
                  >
                    Zamítnout
                  </button>
                </div>
              </div>
              ))}
            </div>
            {getTotalPages(deferredRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('deferred')}
                totalPages={getTotalPages(deferredRequests)}
                onPageChange={(page) => setSectionPage('deferred', page)}
                itemsPerPage={itemsPerPage}
                totalItems={deferredRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné odložené žádosti</p>
        )}

        <h2>Zamítnuté žádosti ({rejectedByEconomistRequests.length})</h2>
        {rejectedByEconomistRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(rejectedByEconomistRequests, getSectionPage('rejected')).map(request => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                getStatusLabel={getStatusLabel}
                getStatusClass={getStatusClass}
              />
              ))}
            </div>
            {getTotalPages(rejectedByEconomistRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('rejected')}
                totalPages={getTotalPages(rejectedByEconomistRequests)}
                onPageChange={(page) => setSectionPage('rejected', page)}
                itemsPerPage={itemsPerPage}
                totalItems={rejectedByEconomistRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné zamítnuté žádosti</p>
        )}

        <h2>Schváleno ekonomem ({approvedByEconomistRequests.length})</h2>
        {approvedByEconomistRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(approvedByEconomistRequests, getSectionPage('approved_economist')).map(request => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  getStatusLabel={getStatusLabel}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
            {getTotalPages(approvedByEconomistRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('approved_economist')}
                totalPages={getTotalPages(approvedByEconomistRequests)}
                onPageChange={(page) => setSectionPage('approved_economist', page)}
                itemsPerPage={itemsPerPage}
                totalItems={approvedByEconomistRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné schválené žádosti</p>
        )}

        <h2>Schváleno ředitelem ({approvedByDirectorRequests.length})</h2>
        {approvedByDirectorRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(approvedByDirectorRequests, getSectionPage('approved_director')).map(request => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  getStatusLabel={getStatusLabel}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
            {getTotalPages(approvedByDirectorRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('approved_director')}
                totalPages={getTotalPages(approvedByDirectorRequests)}
                onPageChange={(page) => setSectionPage('approved_director', page)}
                itemsPerPage={itemsPerPage}
                totalItems={approvedByDirectorRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné schválené žádosti ředitelem</p>
        )}

        <h2>Zamítnuto ředitelem ({rejectedByDirectorRequests.length})</h2>
        {rejectedByDirectorRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(rejectedByDirectorRequests, getSectionPage('rejected_director')).map(request => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  getStatusLabel={getStatusLabel}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
            {getTotalPages(rejectedByDirectorRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('rejected_director')}
                totalPages={getTotalPages(rejectedByDirectorRequests)}
                onPageChange={(page) => setSectionPage('rejected_director', page)}
                itemsPerPage={itemsPerPage}
                totalItems={rejectedByDirectorRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné zamítnuté žádosti ředitelem</p>
        )}

        <h2>Přijato obchodním referentem ({acceptedByReferentRequests.length})</h2>
        {acceptedByReferentRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(acceptedByReferentRequests, getSectionPage('accepted_referent')).map(request => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  getStatusLabel={getStatusLabel}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
            {getTotalPages(acceptedByReferentRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('accepted_referent')}
                totalPages={getTotalPages(acceptedByReferentRequests)}
                onPageChange={(page) => setSectionPage('accepted_referent', page)}
                itemsPerPage={itemsPerPage}
                totalItems={acceptedByReferentRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné přijaté žádosti</p>
        )}

        <h2>Objednáno obchodním referentem ({orderedByReferentRequests.length})</h2>
        {orderedByReferentRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(orderedByReferentRequests, getSectionPage('ordered_referent')).map(request => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  getStatusLabel={getStatusLabel}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
            {getTotalPages(orderedByReferentRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('ordered_referent')}
                totalPages={getTotalPages(orderedByReferentRequests)}
                onPageChange={(page) => setSectionPage('ordered_referent', page)}
                itemsPerPage={itemsPerPage}
                totalItems={orderedByReferentRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné objednané žádosti</p>
        )}

        <h2>Dokončeno ({completedRequests.length})</h2>
        {completedRequests.length > 0 ? (
          <>
            <div className="requests-list">
              {getPaginatedRequests(completedRequests, getSectionPage('completed')).map(request => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  getStatusLabel={getStatusLabel}
                  getStatusClass={getStatusClass}
                />
              ))}
            </div>
            {getTotalPages(completedRequests) > 1 && (
              <Pagination
                currentPage={getSectionPage('completed')}
                totalPages={getTotalPages(completedRequests)}
                onPageChange={(page) => setSectionPage('completed', page)}
                itemsPerPage={itemsPerPage}
                totalItems={completedRequests.length}
              />
            )}
          </>
        ) : (
          <p style={{ padding: '1rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Žádné dokončené žádosti</p>
        )}
      </div>
      )}

      {selectedRequest && actionType && (
        <div className="modal-overlay" onClick={() => {
          setSelectedRequest(null)
          setActionType(null)
          setNotes('')
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              {actionType === 'approve' && 'Schválit žádost'}
              {actionType === 'reject' && 'Zamítnout žádost'}
              {actionType === 'defer' && 'Odložit žádost'}
            </h3>
            <div className="modal-request-info">
              <p><strong>Předmět:</strong> {selectedRequest.subject}</p>
              <p><strong>Středisko:</strong> {selectedRequest.center_name}</p>
            </div>
            <div className="modal-form">
              <label>
                Poznámky (volitelné):
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Zadejte poznámky..."
                />
              </label>
              <div className="modal-actions">
                <button 
                  className={`btn-${actionType === 'approve' ? 'approve' : actionType === 'reject' ? 'reject' : 'defer'}`}
                  onClick={handleAction}
                >
                  {actionType === 'approve' && 'Schválit'}
                  {actionType === 'reject' && 'Zamítnout'}
                  {actionType === 'defer' && 'Odložit'}
                </button>
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setSelectedRequest(null)
                    setActionType(null)
                    setNotes('')
                  }}
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FinancistView

